/* eslint-disable @atlaskit/design-system/no-legacy-icons */
/* eslint-disable filenames/match-regex */
import { type DatabaseFieldIconType, FIELD_ICONS } from '@databases/icons/FieldTypeIcon';
import { getFieldTypeName } from '@databases/intl/field-type-names';
import { messages } from '@databases/intl/messages';
import { type DefaultRootState } from '@databases/redux';
import { type ObjectMode } from '@databases/types';
import {
  DatabaseFieldType,
  type ValueWithContext,
  type DatabaseField,
} from '@databases/types/database';
import { getReferencedJiraIssueRefs } from '@databases/utils/database/field-types/jiraIssueDetails';
import { getReferencedLabelContentRefs } from '@databases/utils/database/field-types/pageLabels';
import { getReferencedStatusContentRefs } from '@databases/utils/database/field-types/pageStatus';
import React, { type ComponentType, useMemo } from 'react';
import { type MessageDescriptor } from 'react-intl-next';

import { isFeatureFlagEnabled } from '@canvas/client-feature-flags';
import { type ClientFeatureFlagName } from '@canvas/client-feature-flags';
import { getExperimentValue } from '@canvas/client-feature-flags/experiments';

import DateValueEdit from './edit/DateValueEdit';
import NumberValuePopoverEdit from './edit/NumberValuePopoverEdit';
import RichTextValuePopoverEdit from './edit/RichTextValuePopoverEdit';
import TextValuePopoverEdit from './edit/TextValuePopoverEdit';
import AttachmentPopoverEdit from './edit/attachment-popover/AttachmentPopoverEdit';
import EntryLinkPopoverEdit from './edit/entry-link-popover/EntryLinkPopoverEdit';
import HyperlinkValuePopoverEdit from './edit/hyperlink-popover/HyperlinkPopoverEdit';
import JiraAssigneeValueEdit from './edit/jira-assignee/JiraAssigneeValueEdit';
import JiraIssueValuePopoverEdit from './edit/jira-issue-popover/JiraIssuePopoverEdit';
import JiraPriorityValueEdit from './edit/jira-priority/JiraPriorityValueEdit';
import JiraStatusValueEdit from './edit/jira-status/JiraStatusValueEdit';
import JiraSummaryValueEdit from './edit/jira-summary/JiraSummaryValueEdit';
import PageLabelsPopoverEdit from './edit/page-labels-popover/PageLabelsPopoverEdit';
import PageLinkValuePopoverEdit from './edit/page-link-popover/PageLinkPopoverEdit';
import PageStatusPopoverEdit from './edit/page-status-popover/PageStatusPopoverEdit';
import TagPopoverEdit from './edit/tag-popover/TagPopoverEdit';
import { type EditComponentProps } from './edit/types';
import UserValuePopoverEdit from './edit/user-popover/UserPopoverEdit';
import AttachmentValueView from './view/AttachmentValueView';
import BacklinkValueView from './view/BacklinkValueView';
import CheckboxValueView from './view/CheckboxValueView';
import ContentDetailsValueView from './view/ContentDetailsValueView';
import DateValueView from './view/DateValueView';
import EntryDetailsValueView from './view/EntryDetailsValueView';
import EntryLinkValueView from './view/EntryLinkValueView';
import HyperlinkValueView from './view/HyperlinkValueView';
import JiraIssueDetailsValueView from './view/JiraIssueDetailsValueView';
import JiraIssueValueView from './view/JiraIssueValueView';
import JiraSummaryValueView from './view/JiraSummaryValueView';
import NumberValueView from './view/NumberValueView';
import PageLinkValueView from './view/PageLinkValueView';
import RichTextValueView from './view/RichTextValueView';
import SelectValueView from './view/SelectValueView';
import TextValueView from './view/TextValueView';
import UserValueView from './view/UserValueView';
import { type ViewComponentProps } from './view/types';

const TextFieldView: React.ComponentType<ViewComponentProps<DatabaseFieldType.TEXT>> = (
  props
) => {
  const isRichText = useMemo(
    () =>
      getExperimentValue('databasesRichText', 'isEnabled', {
        fireExperimentExposure: false,
      }),
    []
  );
  return isRichText ? <RichTextValueView {...props} /> : <TextValueView {...props} />;
};

const TextFieldPopoverEdit: React.ComponentType<
  EditComponentProps<DatabaseFieldType.TEXT>
> = (props) => {
  const isRichText = useMemo(
    () =>
      getExperimentValue('databasesRichText', 'isEnabled', {
        fireExperimentExposure: false,
      }),
    []
  );
  return isRichText && !props.isFilterValueEdit ? (
    <RichTextValuePopoverEdit {...props} />
  ) : (
    <TextValuePopoverEdit {...props} />
  );
};

const HiddenAiSummaryValueView: React.ComponentType<
  ViewComponentProps<DatabaseFieldType.AI_SUMMARY>
> = () => null;

const HiddenAiSummaryValueEdit: React.ComponentType<
  EditComponentProps<DatabaseFieldType.AI_SUMMARY>
> = () => null;

export enum FieldTypeCategory {
  BASIC = 'Basic',
  PAGE = 'Page',
  JIRA = 'Jira',
  OTHER_DATABASES = 'Other Databases',
}

export enum EditMode {
  /** Values of this type are not editable; the view component is always rendered. */
  READ_ONLY,

  /** Values of this type can switch between the view and edit component when it is focused/blurred. */
  INLINE_EDIT,

  /** The (seamless) edit component of this value should always be rendered (if the database is editable). */
  EDIT,
}

export interface FieldType<Type extends DatabaseFieldType> extends DatabaseFieldIconType {
  label: MessageDescriptor;
  description?: MessageDescriptor;
  shortDescription?: MessageDescriptor;
  errorDescription?: MessageDescriptor;
  defaultText: MessageDescriptor;
  category: FieldTypeCategory;
  view: ComponentType<ViewComponentProps<Type>>;
  popoverEdit?: ComponentType<EditComponentProps<Type>>;
  edit?: ComponentType<EditComponentProps<Type>>;
  editMode: (
    context: ValueWithContext<Type, ObjectMode, Type>,
    state: DefaultRootState
  ) => EditMode;
}

type PartialFeatureFlags = Partial<Record<ClientFeatureFlagName, boolean>>;
type FieldTypes = { [Type in DatabaseFieldType]: FieldType<Type> };

/**
 * This config is now a static object in memory that only gets rebuilt
 * and set on demand. By default this is set without any flags on.
 *
 * @see services/databases/src/bootstrap/initialize-field-types-config.ts
 */
let fieldTypesConfig: FieldTypes = buildFieldTypesConfig({});

/**
 * When feature flags change this will need to rebuilt.
 * This currently can only happen from dev tools, so it should not be often.
 */
export function rebuildFieldTypesConfig(flags: PartialFeatureFlags) {
  fieldTypesConfig = buildFieldTypesConfig(flags);
}

export function getFieldTypes(): { [Type in DatabaseFieldType]: FieldType<Type> } {
  return fieldTypesConfig;
}

export function getFieldType<T extends DatabaseFieldType>(type: T): FieldType<T> {
  if (!Object.hasOwn(fieldTypesConfig, type)) {
    throw new Error(`Unknown field type ${type}.`);
  }

  return fieldTypesConfig[type];
}

function isFlagEnabled(flags: PartialFeatureFlags, flag: ClientFeatureFlagName): boolean {
  return flag in flags && flags[flag] === true;
}

function shouldUseJiraSummaryView(
  field: DatabaseField<DatabaseFieldType.JIRA_ISSUE_DETAILS>,
  twoWayEnabled: boolean
): boolean {
  return field.settings.jiraField === 'summary' && twoWayEnabled;
}

function computeJiraIssueDetailsEditMode(
  context: ValueWithContext<
    DatabaseFieldType.JIRA_ISSUE_DETAILS,
    ObjectMode,
    DatabaseFieldType.JIRA_ISSUE_DETAILS
  >,
  state: DefaultRootState,
  twoWayEnabled: boolean
): EditMode {
  if (!twoWayEnabled) {
    return EditMode.READ_ONLY;
  }

  const field = context.field;

  // For summary fields, check if there are multiple issues and disable edit if so
  if (field.settings.jiraField === 'summary') {
    const refs = getReferencedJiraIssueRefs(context, state);
    if (refs.length > 1) {
      return EditMode.READ_ONLY;
    }
  }

  if (
    ['summary', 'status', 'priority', 'assignee'].includes(field.settings.jiraField ?? '')
  ) {
    return EditMode.INLINE_EDIT;
  }
  return EditMode.READ_ONLY;
}

/**
 * Rebuild the field types config based on flags passed to it.
 */
function buildFieldTypesConfig(flags: PartialFeatureFlags): FieldTypes {
  const isLiveDocEnabled = isFlagEnabled(flags, 'databases-frontend-live-doc');

  return {
    // Basic
    [DatabaseFieldType.TEXT]: {
      ...FIELD_ICONS[DatabaseFieldType.TEXT],
      label: getFieldTypeName(DatabaseFieldType.TEXT),
      shortDescription: messages['field-settings.type-text-short-description'],
      defaultText: messages['field.default-name.text'],
      category: FieldTypeCategory.BASIC,
      view: TextFieldView,
      editMode: () => EditMode.INLINE_EDIT,
      popoverEdit: TextFieldPopoverEdit,
    },
    [DatabaseFieldType.AI_SUMMARY]: {
      ...FIELD_ICONS[DatabaseFieldType.AI_SUMMARY],
      label: getFieldTypeName(DatabaseFieldType.AI_SUMMARY),
      shortDescription: messages['field-settings.type-ai-summary-short-description'],
      defaultText: messages['field.default-name.ai-summary'],
      category: FieldTypeCategory.BASIC,
      view: HiddenAiSummaryValueView,
      edit: HiddenAiSummaryValueEdit,
      editMode: () => EditMode.READ_ONLY,
    },
    [DatabaseFieldType.ATTACHMENT]: {
      ...FIELD_ICONS[DatabaseFieldType.ATTACHMENT],
      label: getFieldTypeName(DatabaseFieldType.ATTACHMENT),
      defaultText: messages['field.default-name.media'],
      category: FieldTypeCategory.BASIC,
      view: AttachmentValueView,
      editMode: () => EditMode.INLINE_EDIT,
      popoverEdit: AttachmentPopoverEdit,
    },
    [DatabaseFieldType.NUMBER]: {
      ...FIELD_ICONS[DatabaseFieldType.NUMBER],
      label: getFieldTypeName(DatabaseFieldType.NUMBER),
      defaultText: messages['field.default-name.number'],
      category: FieldTypeCategory.BASIC,
      view: NumberValueView,
      editMode: () => EditMode.INLINE_EDIT,
      popoverEdit: NumberValuePopoverEdit,
    },
    [DatabaseFieldType.DATE]: {
      ...FIELD_ICONS[DatabaseFieldType.DATE],
      label: getFieldTypeName(DatabaseFieldType.DATE),
      defaultText: messages['field.default-name.date'],
      category: FieldTypeCategory.BASIC,
      view: DateValueView,
      edit: DateValueEdit,
      editMode: () => EditMode.INLINE_EDIT,
    },
    [DatabaseFieldType.SELECT]: {
      ...FIELD_ICONS[DatabaseFieldType.SELECT],
      label: getFieldTypeName(DatabaseFieldType.SELECT),
      shortDescription: messages['field-settings.type-tag-short-description'],
      defaultText: messages['field.default-name.tag'],
      category: FieldTypeCategory.BASIC,
      view: SelectValueView,
      popoverEdit: TagPopoverEdit,
      editMode: () => EditMode.INLINE_EDIT,
    },
    [DatabaseFieldType.USER]: {
      ...FIELD_ICONS[DatabaseFieldType.USER],
      label: getFieldTypeName(DatabaseFieldType.USER),
      shortDescription: messages['field-settings.type-user-short-description'],
      defaultText: messages['field.default-name.user'],
      category: FieldTypeCategory.BASIC,
      view: UserValueView,
      editMode: () => EditMode.INLINE_EDIT,
      popoverEdit: UserValuePopoverEdit,
    },
    [DatabaseFieldType.HYPERLINK]: {
      ...FIELD_ICONS[DatabaseFieldType.HYPERLINK],
      label: getFieldTypeName(DatabaseFieldType.HYPERLINK),
      shortDescription: messages['field-settings.type-link-short-description'],
      defaultText: messages['field.default-name.hyperlink'],
      category: FieldTypeCategory.BASIC,
      view: HyperlinkValueView,
      popoverEdit: HyperlinkValuePopoverEdit,
      editMode: () => EditMode.INLINE_EDIT,
    },
    [DatabaseFieldType.CHECKBOX]: {
      ...FIELD_ICONS[DatabaseFieldType.CHECKBOX],
      label: getFieldTypeName(DatabaseFieldType.CHECKBOX),
      shortDescription: messages['field-settings.type-checkbox-short-description'],
      defaultText: messages['field.default-name.checkbox'],
      category: FieldTypeCategory.BASIC,
      view: CheckboxValueView,
      edit: (props) => <CheckboxValueView {...props} recursionCount={0} />,
      editMode: () => EditMode.EDIT,
    },
    [DatabaseFieldType.PAGE_LINK]: {
      ...FIELD_ICONS[DatabaseFieldType.PAGE_LINK],
      label: getFieldTypeName(DatabaseFieldType.PAGE_LINK, isLiveDocEnabled),
      shortDescription: isLiveDocEnabled
        ? messages['field-settings.type-content-link-short-description']
        : messages['field-settings.type-page-link-short-description'],
      defaultText: messages['field.default-name.page-link'],
      category: FieldTypeCategory.PAGE,
      view: PageLinkValueView,
      editMode: () => EditMode.INLINE_EDIT,
      popoverEdit: PageLinkValuePopoverEdit,
    },
    [DatabaseFieldType.PAGE_STATUS]: {
      ...FIELD_ICONS[DatabaseFieldType.PAGE_STATUS],
      label: getFieldTypeName(DatabaseFieldType.PAGE_STATUS, isLiveDocEnabled),
      description: isLiveDocEnabled
        ? messages['field-settings.type-page-and-live-doc-status-description']
        : messages['field-settings.type-page-status-description'],
      shortDescription: isLiveDocEnabled
        ? messages['field-settings.type-content-status-short-description']
        : messages['field-settings.type-page-status-short-description'],
      errorDescription: isLiveDocEnabled
        ? messages['field-settings.type-status-error-description']
        : messages['field-settings.type-page-status-error-description'],
      defaultText: messages['field.default-name.page-status'],
      category: FieldTypeCategory.PAGE,
      view: (props) => <ContentDetailsValueView {...props} />,
      editMode: ({ database, field, entry }, state) =>
        !field.settings.allowEdit ||
        getReferencedStatusContentRefs({ database, field, entry }, state).length === 0
          ? EditMode.READ_ONLY
          : EditMode.INLINE_EDIT,
      popoverEdit: PageStatusPopoverEdit,
    },
    [DatabaseFieldType.PAGE_LABELS]: {
      ...FIELD_ICONS[DatabaseFieldType.PAGE_LABELS],
      label: getFieldTypeName(DatabaseFieldType.PAGE_LABELS, isLiveDocEnabled),
      description: isLiveDocEnabled
        ? messages['field-settings.type-page-and-live-doc-label-description']
        : messages['field-settings.type-page-label-description'],
      shortDescription: isLiveDocEnabled
        ? messages['field-settings.type-content-label-short-description']
        : messages['field-settings.type-page-label-short-description'],
      errorDescription: isLiveDocEnabled
        ? messages['field-settings.type-label-error-description']
        : messages['field-settings.type-page-label-error-description'],
      defaultText: messages['field.default-name.page-labels'],
      category: FieldTypeCategory.PAGE,
      view: (props) => <ContentDetailsValueView {...props} />,
      editMode: ({ database, field, entry }, state) =>
        !field.settings.allowEdit ||
        getReferencedLabelContentRefs({ database, field, entry }, state).length === 0
          ? EditMode.READ_ONLY
          : EditMode.INLINE_EDIT,
      popoverEdit: PageLabelsPopoverEdit,
    },
    [DatabaseFieldType.PAGE_DETAILS]: {
      ...FIELD_ICONS[DatabaseFieldType.PAGE_DETAILS],
      label: getFieldTypeName(DatabaseFieldType.PAGE_DETAILS, isLiveDocEnabled),
      description: isLiveDocEnabled
        ? messages['field-settings.type-page-and-live-doc-details-description']
        : messages['field-settings.type-page-details-description'],
      shortDescription: isLiveDocEnabled
        ? messages['field-settings.type-content-details-short-description']
        : messages['field-settings.type-page-details-short-description'],
      errorDescription: isLiveDocEnabled
        ? messages['field-settings.type-details-error-description']
        : messages['field-settings.type-page-details-error-description'],
      defaultText: messages['field.default-name.page-details'],
      category: FieldTypeCategory.PAGE,
      view: (props) => <ContentDetailsValueView {...props} />,
      edit: () => null,
      editMode: () => EditMode.READ_ONLY,
    },
    [DatabaseFieldType.ENTRY_LINK]: {
      ...FIELD_ICONS[DatabaseFieldType.ENTRY_LINK],
      label: getFieldTypeName(DatabaseFieldType.ENTRY_LINK),
      description: messages['field-settings.type-entry-link-description'],
      shortDescription: messages['field-settings.type-entry-link-short-description'],
      defaultText: messages['field.default-name.entry-link'],
      category: FieldTypeCategory.OTHER_DATABASES,
      view: EntryLinkValueView,
      popoverEdit: EntryLinkPopoverEdit,
      editMode: () => EditMode.INLINE_EDIT,
    },
    [DatabaseFieldType.BACKLINK]: {
      ...FIELD_ICONS[DatabaseFieldType.BACKLINK],
      label: getFieldTypeName(DatabaseFieldType.BACKLINK),
      description: messages['field-settings.type-entry-backlink-description'],
      shortDescription: messages['field-settings.type-entry-backlink-short-description'],
      defaultText: messages['field.default-name.backlink'],
      category: FieldTypeCategory.OTHER_DATABASES,
      view: BacklinkValueView,
      edit: () => null,
      editMode: () => EditMode.READ_ONLY,
    },
    [DatabaseFieldType.ENTRY_DETAILS]: {
      ...FIELD_ICONS[DatabaseFieldType.ENTRY_DETAILS],
      label: getFieldTypeName(DatabaseFieldType.ENTRY_DETAILS),
      description: messages['field-settings.type-entry-details-description'],
      shortDescription: messages['field-settings.type-entry-details-short-description'],
      errorDescription: messages['field-settings.type-entry-details-error-description'],
      defaultText: messages['field.default-name.entry-details'],
      category: FieldTypeCategory.OTHER_DATABASES,
      view: EntryDetailsValueView,
      edit: () => null,
      editMode: () => EditMode.READ_ONLY,
    },
    [DatabaseFieldType.JIRA_ISSUE]: {
      ...FIELD_ICONS[DatabaseFieldType.JIRA_ISSUE],
      label: getFieldTypeName(DatabaseFieldType.JIRA_ISSUE),
      shortDescription: isFeatureFlagEnabled('canvas-issue-terminology-refresh-update')
        ? messages[
            'field-settings.type-jira-issue-short-description-issue-terminology-refresh-m4'
          ]
        : messages['field-settings.type-jira-issue-short-description'],
      defaultText: messages['field.default-name.jira-issue'],
      category: FieldTypeCategory.JIRA,
      view: JiraIssueValueView,
      editMode: () => EditMode.INLINE_EDIT,
      popoverEdit: JiraIssueValuePopoverEdit,
    },
    [DatabaseFieldType.JIRA_ISSUE_DETAILS]: {
      ...FIELD_ICONS[DatabaseFieldType.JIRA_ISSUE_DETAILS],
      label: getFieldTypeName(DatabaseFieldType.JIRA_ISSUE_DETAILS),
      description: isFeatureFlagEnabled('canvas-issue-terminology-refresh-update')
        ? messages[
            'field-settings.type-jira-issue-details-description-issue-terminology-refresh-m4'
          ]
        : messages['field-settings.type-jira-issue-details-description'],
      shortDescription: isFeatureFlagEnabled('canvas-issue-terminology-refresh-update')
        ? messages[
            'field-settings.type-jira-issue-details-short-description-issue-terminology-refresh-m4'
          ]
        : messages['field-settings.type-jira-issue-details-short-description'],
      errorDescription: isFeatureFlagEnabled('canvas-issue-terminology-refresh-update')
        ? messages[
            'field-settings.type-jira-issue-details-error-description-issue-terminology-refresh-m4'
          ]
        : messages['field-settings.type-jira-issue-details-error-description'],
      defaultText: messages['field.default-name.jira-issue-details'],
      category: FieldTypeCategory.JIRA,
      view: (props: ViewComponentProps<DatabaseFieldType.JIRA_ISSUE_DETAILS>) => {
        const twoWayEnabled = Boolean(
          getExperimentValue('databases2waySyncEnabled', 'enabled', {
            fireExperimentExposure: true,
          })
        );
        return shouldUseJiraSummaryView(
          props.field as DatabaseField<DatabaseFieldType.JIRA_ISSUE_DETAILS>,
          twoWayEnabled
        ) ? (
          <JiraSummaryValueView {...props} />
        ) : (
          <JiraIssueDetailsValueView {...props} />
        );
      },
      popoverEdit: (props: EditComponentProps<DatabaseFieldType.JIRA_ISSUE_DETAILS>) =>
        props.field.settings.jiraField === 'status' ? (
          <JiraStatusValueEdit {...(props as any)} />
        ) : props.field.settings.jiraField === 'summary' ? (
          <JiraSummaryValueEdit {...props} />
        ) : props.field.settings.jiraField === 'priority' ? (
          <JiraPriorityValueEdit {...props} />
        ) : props.field.settings.jiraField === 'assignee' ? (
          <JiraAssigneeValueEdit {...props} />
        ) : undefined,
      editMode: (context, state) => {
        const twoWayEnabled = Boolean(
          getExperimentValue('databases2waySyncEnabled', 'enabled', {
            fireExperimentExposure: true,
          })
        );
        return computeJiraIssueDetailsEditMode(context, state, twoWayEnabled);
      },
    },
  };
}
